import React from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { selectAccessToken } from '../../containers/App/selectors';

import {
  selectGeneralStats,
  selectGraphResults,
  selectGraphSpinner,
  selectLimitStats,
  selectMonthStats,
  selectPlayerToShowStats,
  selectTableSizeStats,
} from '../../containers/StatPage/selectors';
import type { StatView } from '../data/demoStats';
import { findStats } from '../../containers/StatPage/actions';
import { getPlayerStatsHref, pokerSitePostfix } from '../../containers/StatPage/utils';
import formatHands from '../../utils/formatHands';
import formatWinnings from '../../utils/formatWinnings';
import V2PostflopPanel from './V2PostflopPanel';
import {
  addUltraGridRow,
  buildUltraDisplayEntries,
  normalizeUltraPrefs,
  readUltraPrefs,
  resolveUltraHeadStyle,
  resolveUltraItemStyle,
  setUltraGridSlot,
  type UltraPrefs,
  writeUltraPrefs,
} from '../utils/ultraPrefs';
import { buildUltraStatCatalogEntries } from '../utils/ultraStatCatalog';
import { pushUltraStorageToServer, readUltraHuds } from '../utils/ultraHuds';
import V2UltraStylePanel from './V2UltraStylePanel';
import V2UltraStatPicker from './V2UltraStatPicker';

const ULTRA_MODE_KEY = 'portableUltraMode';

type PreflopStatKey =
  | 'threebet'
  | 'foldedtothreebetpreflop'
  | 'squeeze'
  | 'raisedthreebetpreflop'
  | 'foldedtofourbetpreflop'
  | 'raisedfourbetpreflop'
  | 'sawshowdown'
  | 'wonwhensawflop'
  | 'wonshowdown';

function getTextNameOfLimit(limit: number) {
  return `NL${limit}`;
}

function getTextNameOfTableSize(tableSize: 2 | 6 | 9) {
  if (tableSize === 2) return 'HU';
  if (tableSize === 6) return 'SH';
  return 'FR';
}

const preflopOverview: Array<[string, PreflopStatKey]> = [
  ['3bet', 'threebet'],
  ['F3bet', 'foldedtothreebetpreflop'],
  ['SQZ', 'squeeze'],
  ['4bet', 'raisedthreebetpreflop'],
  ['F4bet', 'foldedtofourbetpreflop'],
  ['5bet', 'raisedfourbetpreflop'],
  ['WTSD', 'sawshowdown'],
  ['WWSF', 'wonwhensawflop'],
  ['W$SD', 'wonshowdown'],
];

function shouldUseNativeLink(event: React.MouseEvent<HTMLAnchorElement>) {
  return event.button !== 0 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;
}

const V2PortableStats: React.FC = () => {
  const dispatch = useDispatch();
  const accessToken = useSelector(selectAccessToken);
  const player = useSelector(selectPlayerToShowStats);
  const stats = useSelector(selectGeneralStats);
  const graph = useSelector(selectGraphResults);
  const graphLoading = useSelector(selectGraphSpinner);
  const monthStats = useSelector(selectMonthStats);
  const tableSizeStats = useSelector(selectTableSizeStats);
  const limitStats = useSelector(selectLimitStats);
  const [view, setView] = React.useState<StatView>('overview');
  const [ultraMode, setUltraMode] = React.useState(
    () => localStorage.getItem(ULTRA_MODE_KEY) === 'true',
  );
  const [ultraPrefs, setUltraPrefs] = React.useState<UltraPrefs>(() =>
    normalizeUltraPrefs(readUltraPrefs()),
  );
  const [ultraStyleOpen, setUltraStyleOpen] = React.useState(false);
  const [ultraEditRows, setUltraEditRows] = React.useState(false);
  const [pickerSlotIndex, setPickerSlotIndex] = React.useState<number | null>(null);

  React.useEffect(() => {
    localStorage.setItem(ULTRA_MODE_KEY, String(ultraMode));
    if (!ultraMode) {
      setUltraStyleOpen(false);
      setUltraEditRows(false);
      setPickerSlotIndex(null);
    }
  }, [ultraMode]);

  React.useEffect(() => {
    if (!ultraEditRows) {
      setPickerSlotIndex(null);
    }
  }, [ultraEditRows]);

  React.useEffect(() => {
    if (!accessToken || !ultraMode) {
      return;
    }

    const timer = window.setTimeout(() => {
      void pushUltraStorageToServer(accessToken, readUltraHuds(), ultraPrefs).catch(
        () => undefined,
      );
    }, 900);

    return () => window.clearTimeout(timer);
  }, [accessToken, ultraMode, ultraPrefs]);

  const ultraCatalog = React.useMemo(() => {
    if (!ultraMode) {
      return [];
    }

    return buildUltraStatCatalogEntries(stats, ultraPrefs);
  }, [stats, ultraMode, ultraPrefs]);

  const ultraDisplayByKey = React.useMemo(() => {
    const baseEntries = ultraCatalog
      .filter((entry) => !entry.key.startsWith('custom:'))
      .map(({ label, value }) => ({ label, value }));

    return new Map(
      buildUltraDisplayEntries(baseEntries, ultraPrefs).map((entry) => [entry.key, entry]),
    );
  }, [ultraCatalog, ultraPrefs]);

  const ultraPickerOptions = React.useMemo(
    () =>
      Array.from(ultraDisplayByKey.values())
        .map((entry) => ({
          key: entry.key,
          label: entry.label,
          value: entry.value,
        }))
        .sort((left, right) => left.label.localeCompare(right.label, 'ru')),
    [ultraDisplayByKey],
  );

  const ultraGridSlots = ultraPrefs.gridSlots;
  const ultraHeadStyle = React.useMemo(() => resolveUltraHeadStyle(ultraPrefs), [ultraPrefs]);

  const handleUltraPrefsChange = React.useCallback((next: UltraPrefs) => {
    const normalized = normalizeUltraPrefs(next);
    setUltraPrefs(normalized);
    writeUltraPrefs(normalized);
  }, []);

  const safeStats = stats ?? {};

  const formatSummaryRow = React.useCallback(
    (row: {
      key: string;
      hands: number | null | undefined;
      winnings: number | null | undefined;
      bb100?: string | number | null;
      vpip?: string | number | null;
      pfr?: string | number | null;
      wwsf?: string | number | null;
    }) => ({
      key: row.key,
      hands: row.hands == null ? '—' : formatHands(row.hands, 1),
      winnings: row.winnings == null ? '—' : formatWinnings(row.winnings, 1, false),
      winningsClass:
        row.winnings == null
          ? ''
          : row.winnings >= 0
            ? ('positive' as const)
            : ('negative' as const),
      bb100: row.bb100 ?? '—',
      vpip: row.vpip ?? '—',
      pfr: row.pfr ?? '—',
      wwsf: row.wwsf ?? '—',
    }),
    [],
  );

  const formatSummaryRows = React.useCallback(
    (
      rows: Array<{
        key: string;
        hands: number | null | undefined;
        winnings: number | null | undefined;
        bb100?: string | number | null;
        vpip?: string | number | null;
        pfr?: string | number | null;
        wwsf?: string | number | null;
      }>,
    ) => rows.map(formatSummaryRow),
    [formatSummaryRow],
  );

  const tableSizeSummaryRows = React.useMemo(
    () =>
      formatSummaryRows(
        tableSizeStats.map((row) => ({
          key: getTextNameOfTableSize(row.tableSize as 2 | 6 | 9),
          hands: row.hands,
          winnings: row.winnings,
          bb100: row.bb100,
          vpip: row.vpip,
          pfr: row.pfr,
          wwsf: row.wonwhensawflop,
        })),
      ),
    [formatSummaryRows, tableSizeStats],
  );

  const limitSummaryRows = React.useMemo(
    () =>
      formatSummaryRows(
        limitStats.map((row) => ({
          key: getTextNameOfLimit(row.limit),
          hands: row.hands,
          winnings: row.winnings,
          bb100: row.bb100,
          vpip: row.vpip,
          pfr: row.pfr,
          wwsf: row.wonwhensawflop,
        })),
      ),
    [formatSummaryRows, limitStats],
  );

  const hasSummaryData = tableSizeSummaryRows.length > 0 || limitSummaryRows.length > 0;

  if (!player) {
    return (
      <section className="portable-empty-state" aria-label="Поиск игрока">
        <p className="portable-empty-state__title">Найдите игрока</p>
        <p className="portable-empty-state__hint">
          Введите ник в поле поиска выше и выберите игрока из списка.
        </p>
      </section>
    );
  }

  const winningsType =
    safeStats.winnings == null ? '' : safeStats.winnings >= 0 ? 'positive' : 'negative';

  return (
    <div className={classNames('portable-stats', ultraMode && 'portable-stats--ultra')}>
      <section className="portable-overview" aria-label="Обзор игрока">
        <div className="portable-overview-head">
          <span
            className="portable-player-name"
            title={player.playername}
            style={ultraMode ? { color: ultraPrefs.headColors.playerNameColor } : undefined}
          >
            {player.playername}
          </span>
          <div className="portable-overview-head__tail">
            {view === 'overview' && ultraMode ? (
              <div
                className="portable-ultra-head-stats"
                aria-label="Ключевые показатели"
                style={ultraHeadStyle}
              >
                <span className="portable-ultra-head-stats__item">
                  <span className="portable-ultra-head-stats__value">
                    {safeStats.vpip ?? '—'}/{safeStats.pfr ?? '—'}
                  </span>
                </span>
                <span className="portable-ultra-head-stats__item">
                  <span className={classNames('portable-ultra-head-stats__value', winningsType)}>
                    {safeStats.winnings == null ? '—' : formatWinnings(safeStats.winnings, 1, true)}
                  </span>
                </span>
                <span className="portable-ultra-head-stats__item">
                  <span className="portable-ultra-head-stats__value">
                    {safeStats.hands == null ? '—' : formatHands(safeStats.hands, 1)}
                  </span>
                  <span className="portable-ultra-head-stats__suffix">h</span>
                </span>
              </div>
            ) : null}
            {view === 'overview' ? (
              <div className="portable-overview-head__actions">
                <button
                  type="button"
                  className={classNames(
                    'portable-ultra-btn',
                    ultraMode && 'portable-ultra-btn--active',
                  )}
                  aria-pressed={ultraMode}
                  onClick={() => setUltraMode((value) => !value)}
                >
                  Ultra
                </button>
                {ultraMode ? (
                  <button
                    type="button"
                    className={classNames(
                      'portable-ultra-btn',
                      ultraStyleOpen && 'portable-ultra-btn--active',
                    )}
                    aria-pressed={ultraStyleOpen}
                    aria-label="Настройки Ultra"
                    onClick={() => setUltraStyleOpen((value) => !value)}
                  >
                    Стиль
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        <nav className="view-tabs portable-view-tabs" aria-label="Представления статистики">
          {(['overview', 'history', 'summary'] as StatView[]).map((tab) => (
            <button
              key={tab}
              type="button"
              aria-current={view === tab ? 'page' : undefined}
              onClick={() => setView(tab)}
            >
              {tab === 'overview' ? 'Обзор' : tab === 'history' ? 'История' : 'Summary'}
            </button>
          ))}
        </nav>

        {view === 'overview' && ultraMode && ultraStyleOpen ? (
          <div className="portable-ultra-style-scroll" aria-label="Настройки Ultra">
            <V2UltraStylePanel
              prefs={ultraPrefs}
              onChange={handleUltraPrefsChange}
              onApplyActivePrefs={handleUltraPrefsChange}
              editRows={ultraEditRows}
              onEditRowsChange={setUltraEditRows}
              selectedSlotIndex={pickerSlotIndex}
              selectedStatKey={
                pickerSlotIndex != null ? (ultraGridSlots[pickerSlotIndex] ?? null) : null
              }
              selectedStatLabel={
                pickerSlotIndex != null && ultraGridSlots[pickerSlotIndex]
                  ? (ultraDisplayByKey.get(ultraGridSlots[pickerSlotIndex]!)?.label ?? null)
                  : null
              }
            />
          </div>
        ) : null}

        {view === 'overview' && ultraMode ? (
          <div className="portable-ultra-scroll" aria-label="Ultra статистика">
            <div
              className={classNames(
                'portable-ultra-grid',
                ultraEditRows && 'portable-ultra-grid--edit',
              )}
              style={{ gridTemplateColumns: `repeat(${ultraPrefs.columns}, minmax(0, 1fr))` }}
            >
              {(ultraEditRows
                ? ultraGridSlots.map((slotKey, index) => ({ slotKey, index }))
                : ultraGridSlots
                    .map((slotKey, index) => ({ slotKey, index }))
                    .filter(({ slotKey }) => slotKey && ultraDisplayByKey.has(slotKey))
              ).map(({ slotKey, index }) => {
                const isLastInRow = (index + 1) % ultraPrefs.columns === 0;
                const entry = slotKey ? ultraDisplayByKey.get(slotKey) : null;
                const isSelected = pickerSlotIndex === index;

                if (ultraEditRows) {
                  return (
                    <button
                      type="button"
                      key={`slot-${index}`}
                      className={classNames(
                        'portable-ultra-item',
                        'portable-ultra-item--editable',
                        !entry && 'portable-ultra-item--empty',
                        entry?.isCustom && 'portable-ultra-item--custom',
                        isSelected && 'portable-ultra-item--selected',
                      )}
                      style={{
                        ...resolveUltraItemStyle(ultraPrefs, index, entry?.key ?? `slot-${index}`),
                        borderRight: isLastInRow ? 'none' : undefined,
                      }}
                      onClick={() => setPickerSlotIndex(index)}
                    >
                      {entry ? (
                        <>
                          <span className="portable-ultra-item__label">{entry.label}</span>
                          <span className="portable-ultra-item__value">{entry.value}</span>
                        </>
                      ) : (
                        <span className="portable-ultra-item__placeholder">+ stat</span>
                      )}
                    </button>
                  );
                }

                if (!entry) {
                  return null;
                }

                return (
                  <div
                    className={classNames(
                      'portable-ultra-item',
                      entry.isCustom && 'portable-ultra-item--custom',
                    )}
                    key={`slot-${index}`}
                    style={{
                      ...resolveUltraItemStyle(ultraPrefs, index, entry.key),
                      borderRight: isLastInRow ? 'none' : undefined,
                    }}
                  >
                    <span className="portable-ultra-item__label">{entry.label}</span>
                    <span className="portable-ultra-item__value">{entry.value}</span>
                  </div>
                );
              })}
            </div>
            {ultraEditRows && pickerSlotIndex != null ? (
              <V2UltraStatPicker
                slotIndex={pickerSlotIndex}
                options={ultraPickerOptions}
                currentKey={ultraGridSlots[pickerSlotIndex] ?? null}
                onSelect={(key) => {
                  handleUltraPrefsChange(setUltraGridSlot(ultraPrefs, pickerSlotIndex, key));
                  setPickerSlotIndex(null);
                }}
                onClear={() => {
                  handleUltraPrefsChange(setUltraGridSlot(ultraPrefs, pickerSlotIndex, null));
                  setPickerSlotIndex(null);
                }}
                onClose={() => setPickerSlotIndex(null)}
              />
            ) : null}
            {ultraEditRows ? (
              <button
                type="button"
                className="portable-ultra-add-row"
                onClick={() => handleUltraPrefsChange(addUltraGridRow(ultraPrefs))}
              >
                + Добавить строку
              </button>
            ) : null}
          </div>
        ) : view === 'overview' ? (
          <>
            <section
              className="stats-row stats-row--primary stats-row--portable"
              aria-label="Ключевые показатели"
            >
              <div className="stat-cell stat-cell--vpip">
                <p className="stat-cell__label">VPIP / PFR</p>
                <p className="stat-cell__value stat-cell__value--ratio portable-vpip-pfr">
                  <span className="portable-vpip-pfr__value">{safeStats.vpip ?? '—'}</span>
                  <span className="portable-vpip-pfr__sep" aria-hidden="true">
                    /
                  </span>
                  <span className="portable-vpip-pfr__value">{safeStats.pfr ?? '—'}</span>
                </p>
              </div>
              <div className="stat-cell">
                <p className="stat-cell__label">Hands</p>
                <p className="stat-cell__value">
                  {safeStats.hands == null ? '—' : formatHands(safeStats.hands, 1)}
                </p>
              </div>
              <div className="stat-cell">
                <p className="stat-cell__label">W</p>
                <p className={`stat-cell__value ${winningsType}`}>
                  {safeStats.winnings == null ? '—' : formatWinnings(safeStats.winnings, 1, true)}
                </p>
              </div>
              <div className="stat-cell">
                <p className="stat-cell__label">WR</p>
                <p className={`stat-cell__value ${winningsType}`}>{safeStats.bb100 ?? '—'}</p>
              </div>
            </section>

            {player.sameNickPlayers.length > 0 ? (
              <p className="portable-same-nick">
                {player.sameNickPlayers.map((samePlayer, index) => (
                  <React.Fragment key={samePlayer.id}>
                    {index > 0 ? ', ' : ''}
                    <a
                      href={getPlayerStatsHref(samePlayer.playername, samePlayer.pokersite_id)}
                      onClick={(event) => {
                        if (shouldUseNativeLink(event)) return;
                        event.preventDefault();
                        dispatch(
                          findStats({
                            playername: samePlayer.playername,
                            pokersiteId: samePlayer.pokersite_id,
                          }),
                        );
                      }}
                    >
                      {pokerSitePostfix[samePlayer.pokersite_id]}
                    </a>
                  </React.Fragment>
                ))}
              </p>
            ) : null}

            <div className="portable-preflop-grid">
              {[
                preflopOverview.slice(0, 3),
                preflopOverview.slice(3, 6),
                preflopOverview.slice(6, 9),
              ].map((column) => (
                <div className="portable-preflop-col" key={column[0][0]}>
                  {column.map(([label, key]) => (
                    <div className="stat-cell stat-cell--compact" key={label}>
                      <p className="stat-cell__label">{label}</p>
                      <p className="stat-cell__value">{safeStats[key] ?? '—'}</p>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </>
        ) : view === 'history' ? (
          monthStats.length === 0 ? (
            <p className="table-hint">Нет данных по месяцам для выбранных фильтров.</p>
          ) : (
            <section className="portable-data-section" aria-label="История результатов">
              <ul className="portable-record-list">
                {monthStats.slice(0, 12).map((row) => (
                  <li className="portable-record-card" key={row.month}>
                    <div className="portable-record-card__head">
                      <span className="portable-record-card__title">{row.month}</span>
                      <span
                        className={classNames(
                          'portable-record-card__highlight',
                          row.winnings >= 0 ? 'positive' : 'negative',
                        )}
                      >
                        {formatWinnings(row.winnings, 1, false)}
                      </span>
                    </div>
                    <div className="portable-record-card__metrics portable-record-card__metrics--3">
                      <div className="portable-record-metric">
                        <span className="portable-record-metric__label">Hands</span>
                        <span className="portable-record-metric__value">
                          {formatHands(row.hands, 1)}
                        </span>
                      </div>
                      <div className="portable-record-metric">
                        <span className="portable-record-metric__label">VPIP / PFR</span>
                        <span className="portable-record-metric__value portable-record-metric__value--ratio">
                          {row.vpip ?? '—'}
                          <span className="portable-record-metric__sep">/</span>
                          {row.pfr ?? '—'}
                        </span>
                      </div>
                      <div className="portable-record-metric">
                        <span className="portable-record-metric__label">bb/100</span>
                        <span className="portable-record-metric__value">{row.bb100 ?? '—'}</span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )
        ) : !hasSummaryData ? (
          <p className="table-hint">Нет summary-данных для выбранных фильтров.</p>
        ) : (
          <section className="portable-data-section" aria-label="Summary">
            {tableSizeSummaryRows.length > 0 ? (
              <>
                <p className="portable-record-group-label">Формат</p>
                <ul className="portable-record-list">
                  {tableSizeSummaryRows.map((row) => (
                    <li className="portable-record-card" key={`format-${row.key}`}>
                      <div className="portable-record-card__head">
                        <span className="portable-record-card__title">{row.key}</span>
                        <span
                          className={classNames(
                            'portable-record-card__highlight',
                            row.winningsClass,
                          )}
                        >
                          {row.winnings}
                        </span>
                      </div>
                      <div className="portable-record-card__metrics portable-record-card__metrics--4">
                        <div className="portable-record-metric">
                          <span className="portable-record-metric__label">Hands</span>
                          <span className="portable-record-metric__value">{row.hands}</span>
                        </div>
                        <div className="portable-record-metric">
                          <span className="portable-record-metric__label">bb/100</span>
                          <span className="portable-record-metric__value">{row.bb100}</span>
                        </div>
                        <div className="portable-record-metric">
                          <span className="portable-record-metric__label">VPIP / PFR</span>
                          <span className="portable-record-metric__value portable-record-metric__value--ratio">
                            {row.vpip}
                            <span className="portable-record-metric__sep">/</span>
                            {row.pfr}
                          </span>
                        </div>
                        <div className="portable-record-metric">
                          <span className="portable-record-metric__label">WWSF</span>
                          <span className="portable-record-metric__value">{row.wwsf}</span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
            {limitSummaryRows.length > 0 ? (
              <>
                <p
                  className={classNames(
                    'portable-record-group-label',
                    tableSizeSummaryRows.length > 0 && 'portable-record-group-label--spaced',
                  )}
                >
                  Лимиты
                </p>
                <ul className="portable-record-list">
                  {limitSummaryRows.map((row) => (
                    <li className="portable-record-card" key={`limit-${row.key}`}>
                      <div className="portable-record-card__head">
                        <span className="portable-record-card__title">{row.key}</span>
                        <span
                          className={classNames(
                            'portable-record-card__highlight',
                            row.winningsClass,
                          )}
                        >
                          {row.winnings}
                        </span>
                      </div>
                      <div className="portable-record-card__metrics portable-record-card__metrics--4">
                        <div className="portable-record-metric">
                          <span className="portable-record-metric__label">Hands</span>
                          <span className="portable-record-metric__value">{row.hands}</span>
                        </div>
                        <div className="portable-record-metric">
                          <span className="portable-record-metric__label">bb/100</span>
                          <span className="portable-record-metric__value">{row.bb100}</span>
                        </div>
                        <div className="portable-record-metric">
                          <span className="portable-record-metric__label">VPIP / PFR</span>
                          <span className="portable-record-metric__value portable-record-metric__value--ratio">
                            {row.vpip}
                            <span className="portable-record-metric__sep">/</span>
                            {row.pfr}
                          </span>
                        </div>
                        <div className="portable-record-metric">
                          <span className="portable-record-metric__label">WWSF</span>
                          <span className="portable-record-metric__value">{row.wwsf}</span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </section>
        )}
      </section>

      {view === 'overview' && !ultraMode ? (
        <V2PostflopPanel stats={safeStats} graf={{ graph, loading: graphLoading }} />
      ) : null}
    </div>
  );
};

export default V2PortableStats;
